import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { locationBranchKey } from "../LocationRun/branchKey";
import type { LocationRun } from "../LocationRun/runs";
import type { PartLabelOffset } from "./labels";
import { partLabelOffsets, partLabelsForRun } from "./labels";
import { stayOverlapsBranch } from "../../rail/PartRunPhase/stayOverlap";

/** A site that has not started shows its parts as their own waiting boxes, not chips too. */
function withoutWaitingParts(
  parts: PartLabelOffset[],
  stackRun: LocationRun,
  layout: BookTimelineLayout,
  playheadMs: number,
): PartLabelOffset[] {
  if (playheadMs >= stackRun.startMs) {
    return parts;
  }
  const branchId = locationBranchKey(stackRun, layout);
  const branch = layout.rail.branches.find((item) => item.id === branchId);
  if (branch === undefined) {
    return parts;
  }
  return parts.filter((part) => !layout.rail.stays.some((stay) => (
    stay.placeLabel === part.label && stayOverlapsBranch(stay, branch)
  )));
}

/** Part rows for one painted stack (scene site uses branch occupancy on cast). */
export function partLabelOffsetsForStack(
  stackRun: LocationRun,
  layout: BookTimelineLayout,
  playheadMs: number,
  activePath: readonly string[],
  activeRunId: string | undefined,
): PartLabelOffset[] {
  return withoutWaitingParts(
    occupiedOrActiveParts(stackRun, layout, playheadMs, activePath, activeRunId),
    stackRun,
    layout,
    playheadMs,
  );
}

function occupiedOrActiveParts(
  stackRun: LocationRun,
  layout: BookTimelineLayout,
  playheadMs: number,
  activePath: readonly string[],
  activeRunId: string | undefined,
): PartLabelOffset[] {
  const rail = layout.rail;
  const fromRun = partLabelOffsets(
    stackRun,
    rail,
    playheadMs,
    activePath,
    activeRunId,
  );
  if (fromRun.length > 0) {
    return fromRun;
  }
  const branchId = locationBranchKey(stackRun, layout);
  const branch = rail.branches.find((item) => item.id === branchId);
  if (branch === undefined) {
    return [];
  }
  const branchRun: LocationRun = {
    runId: branch.id,
    locationLabel: branch.label,
    startMs: branch.startMs,
    endMs: branch.endMs,
    lane: branch.lane,
  };
  return partLabelsForRun(
    branchRun,
    rail.branches,
    rail.stays,
    playheadMs,
    activePath,
    activeRunId,
  ).map((label) => ({ label, dx: 0 }));
}
